import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getDashboardUserActivity } from "../../repository/data.repository";
import { DashboardUserActivity } from "../../repository/data.default";
import { renderMetricChart, renderShareRing } from "./MetricChartRender";
import { InsightChartCard, InsightStrip } from "./InsightCards";
import { pickBucket } from "./AnalyticsRange";
import { DashboardSectionScope } from "./DashboardSection";

// Login activity backed by one server-side rollup, split into one focused chart per question:
// how much traffic (total logins), how many people (unique users), how regular they are
// (login frequency), and where the activity concentrates (by company / department). A compact
// stat strip carries the headline numbers, including the active-user rate — the share of
// registered users who actually showed up in the period.
function LoginsCard(props: DashboardSectionScope) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activity, setActivity] = useState<DashboardUserActivity | undefined>();

  const from = props.range?.[0];
  const to = props.range?.[1];
  const bucket = pickBucket(from, to);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDashboardUserActivity({
      companyUid: props.companyUid,
      departmentUid: props.departmentUid,
      from,
      to,
      bucket,
    })
      .then((data) => {
        if (cancelled) return;
        setActivity(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.companyUid, props.departmentUid, from, to, bucket]);

  const avgLoginsPerUser =
    activity && activity.uniqueLogins > 0
      ? Math.round((activity.totalLogins / activity.uniqueLogins) * 10) / 10
      : 0;
  const activeUserRate =
    activity && activity.registeredUsers > 0
      ? Math.round((activity.uniqueLogins / activity.registeredUsers) * 1000) / 10
      : 0;

  // Trend maps for the shared line renderer (bucket start date -> value).
  const byDay = activity?.loginsByDay ?? [];
  const totalTrend = Object.fromEntries(
    byDay.map((d) => [dayjs(d.date).format("YYYY-MM-DD"), d.total]),
  );
  const uniqueTrend = Object.fromEntries(
    byDay.map((d) => [dayjs(d.date).format("YYYY-MM-DD"), d.unique]),
  );

  const frequency = (activity?.loginFrequency ?? []).map((f) => ({
    type: f.band,
    value: f.users,
  }));
  // Named top groups only — the share ring appends the honest "Other" remainder itself.
  const byGroup = (activity?.loginsByGroup ?? []).map((g) => ({
    type: g.label,
    value: g.total,
  }));

  const activeBucket = activity?.bucket ?? bucket;
  const groupTitle =
    (activity?.groupBy ?? (props.companyUid ? "department" : "company")) ===
    "department"
      ? "Logins by department"
      : "Logins by company";

  return (
    <div className="flex w-full flex-col gap-3">
      <InsightStrip
        items={[
          `${(activity?.totalLogins ?? 0).toLocaleString()} total logins`,
          `${(activity?.uniqueLogins ?? 0).toLocaleString()} unique users`,
          `${avgLoginsPerUser} logins/user`,
          `${activeUserRate}% active-user rate`,
        ]}
      />
      <div className="flex flex-row flex-wrap gap-3">
        <InsightChartCard
          title="Total logins"
          subtitle={`By ${activeBucket}`}
          loading={loading}
          error={error}
        >
          {renderMetricChart("line", totalTrend, [], "Logins", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Unique users"
          subtitle={`By ${activeBucket}`}
          loading={loading}
          error={error}
        >
          {renderMetricChart("line", uniqueTrend, [], "Users", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Login frequency"
          subtitle="How often active users signed in during the period"
          loading={loading}
          error={error}
        >
          {renderMetricChart("column", {}, frequency, "Users", 260)}
        </InsightChartCard>
        <InsightChartCard
          title={groupTitle}
          subtitle="Share of login activity — top 10 named, the rest as Other"
          loading={loading}
          error={error}
        >
          {renderShareRing(byGroup, activity?.totalLogins, 260)}
        </InsightChartCard>
      </div>
    </div>
  );
}

export default LoginsCard;
